import React from 'react';

import { useAuth } from '../../api/AuthProvider';
import { useUser } from '../../api/hooks/useUser';
import Anchor from '../../components/Anchor/Anchor';
import { useDictionary } from '../../hooks/useDictionary';
import { colors } from '../../styles/colors';
import LinkViewModel from '../../view-models/LinkViewModel';
import { rem } from '../../styles/rem';
import { FontWeight } from '../../styles/typography';
import { navigationLinkStyle } from './Header';

export type ServiceMenuProps = {
  links?: LinkViewModel[];
  onClick?: () => void;
  loggedIn?: boolean;
  inverted?: boolean;
  staggerItems?: boolean;
  staggerItemOffset?: number;
};

export const MOBILE_MENU_ITEM_INITIAL_DELAY_MS = 80;
export const MOBILE_MENU_ITEM_STAGGER_MS = 28;

export const getMobileMenuItemStyle = (index: number) =>
  ({
    '--mobile-menu-item-delay': `${
      MOBILE_MENU_ITEM_INITIAL_DELAY_MS +
      MOBILE_MENU_ITEM_STAGGER_MS * (index + 1)
    }ms`,
  }) as React.CSSProperties;

const getServiceLinkStyle = (inverted = false) => {
  const backgroundColor = inverted
    ? colors.components.fixedTop.Background.Open
    : colors.components.fixedTop.Background.Default;
  const textColor = inverted
    ? colors.components.fixedTop.Text.Open
    : colors.components.fixedTop.Text.Default;

  return {
    background: [backgroundColor, 'transparent'],
    display: ['block', 'inline-block'],
    fontSize: [rem(48), 2, 2],
    fontWeight: [FontWeight.Normal, FontWeight.Medium, FontWeight.Medium],
    lineHeight: [1.16, 1.71],
    marginLeft: [0, '20px', '24px', '24px'],
    textDecoration: 'none',
    color: [textColor],
    padding: ['12px 20px', 0],
    borderBottom: ['1px solid', 'none'],
    borderBottomColor: [backgroundColor, 'transparent'],
    transition: 'color .3s ease',
    textAlign: ['center', 'left', 'left'],
    fontFamily: 'heading',
    '&:hover': { ...navigationLinkStyle['&:hover'], color: [null, textColor] },
  };
};

const onFocus = () => {
  // If the anchor gets focus, make sure to reset the scroll to the top of the viewport
  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0);
  }
};

function ServiceMenu({
  links,
  onClick,
  inverted,
  staggerItems,
  staggerItemOffset = 0,
}: ServiceMenuProps) {
  return (
    <div>
      {links?.map((link, key) => (
        <Anchor
          prefetch
          key={key}
          onFocus={inverted ? undefined : onFocus}
          onClick={onClick}
          style={
            staggerItems
              ? getMobileMenuItemStyle(staggerItemOffset + key)
              : undefined
          }
          sx={getServiceLinkStyle(inverted)}
          {...link}
        />
      ))}
    </div>
  );
}

export function AuthMenuLink({
  loggedIn,
  onClick,
  inverted,
}: Pick<ServiceMenuProps, 'loggedIn' | 'onClick' | 'inverted'>) {
  const { authenticatedLogoutUrl, logoutUrl } = useAuth();
  const { user, userReady } = useUser();

  const { t } = useDictionary();
  const showLogout = userReady ? !!user : loggedIn === true;
  const isReady = loggedIn === true || userReady;
  const href = userReady ? logoutUrl : authenticatedLogoutUrl;
  const shouldRenderLogout = showLogout || !isReady;

  return (
    <span
      data-auth-logout-placeholder={!isReady ? '' : undefined}
      style={{ display: !isReady ? 'none' : undefined }}
    >
      {shouldRenderLogout ? (
        <Anchor
          onFocus={inverted ? undefined : onFocus}
          onClick={onClick}
          external
          prefetch={false}
          sx={getServiceLinkStyle(inverted)}
          href={href}
          label={t('ui.logout')}
        />
      ) : null}
    </span>
  );
}

ServiceMenu.displayName = 'ServiceMenu';

export default ServiceMenu;
